import { Cancel, Edit, Save } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import {
  GridActionsCellItem,
  GridRowModes,
  type GridColDef,
  type GridColumnGroupingModel,
  type GridRowId,
  type GridRowModel,
  type GridRowModesModel,
} from '@mui/x-data-grid';
import { Button } from '@mui/material';
import { alpha } from '@mui/material/styles';

import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { ThemePalette } from 'mui.theme'; // Assuming this is your custom theme palette
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hook'; // Assuming this is your Redux hook
import { showNotification } from 'store/notification.slice';
import { type Asset } from 'types/asset.types'; // Assuming this is your Asset type
import { isNumber } from 'views/TirePressure/util';
import { convertKpaToPsi, convertCentToFahr } from 'utils/helpers/unitSystem';
import { TPMS_WARNING_CONFIG } from 'utils/tpms';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

// Define a type for the data structure used in AG Grid rows
interface PressureLimitRowData {
  id: string;
  assetType: string;
  make: string;
  model: string;
  minPressure: string | number;
  maxPressure: string | number;
  oemMinPressure: number;
  oemMaxPressure: number;
  minTemperature: string | number;
  maxTemperature: string | number;
  oemMinTemperature: number;
  oemMaxTemperature: number;
  warningPressure: string | number;
  warningTemperature: string | number;
}

interface UpdatePressureLimitsPayloadData {
  selectedType: string;
  selectedMake: string;
  selectedModel: string;
  minPressure: string;
  maxPressure: string;
  warningPressure: string;
  warningTemperature: string;
}

interface PressureLimitModalProps {
  open: boolean;
  handleAbort: () => void;
  enableDarkTheme: boolean;
  assets: Asset[];
  defaultMetricType: string;
  updatePressureLimitForRow: (
    updatedRowData: UpdatePressureLimitsPayloadData
  ) => Promise<void>;
}

const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Asset Info',
    children: [{ field: 'assetType' }, { field: 'make' }, { field: 'model' }],
    headerAlign: 'center',
  },
  {
    groupId: 'OEM Recommended Pressure Range',
    children: [{ field: 'oemMinPressure' }, { field: 'oemMaxPressure' }],
    headerAlign: 'center',
  },
  {
    groupId: 'Current Set Pressure Range',
    children: [
      { field: 'minPressure' },
      ...(TPMS_WARNING_CONFIG.pressure ? [{ field: 'warningPressure' }] : []),
      { field: 'maxPressure' },
    ],
    headerAlign: 'center',
  },
  {
    groupId: 'Actions',
    children: [{ field: 'actions' }],
    headerAlign: 'center',
  },
];

const temperatureColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'Asset Info',
    children: [{ field: 'assetType' }, { field: 'make' }, { field: 'model' }],
    headerAlign: 'center',
  },
  {
    groupId: 'OEM Recommended Temperature Range',
    children: [{ field: 'oemMinTemperature' }, { field: 'oemMaxTemperature' }],
    headerAlign: 'center',
  },
  {
    groupId: 'Current Set Temperature Range',
    children: [
      { field: 'minTemperature' },
      ...(TPMS_WARNING_CONFIG.temperature
        ? [{ field: 'warningTemperature' }]
        : []),
      { field: 'maxTemperature' },
    ],
    headerAlign: 'center',
  },
  {
    groupId: 'Actions',
    children: [{ field: 'actions' }],
    headerAlign: 'center',
  },
];

export const PressureLimitModalTabularView = ({
  open,
  handleAbort,
  enableDarkTheme,
  assets,
  updatePressureLimitForRow,
  defaultMetricType,
}: PressureLimitModalProps) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    customer: { unitSystem },
  } = useAppSelector((state) => state.authReducer);
  const limits = useAppSelector((state) => state.tireProfileReducer.limits);

  const [rowData, setRowData] = useState<PressureLimitRowData[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [metricType, setMetricType] = useState<string>(
    defaultMetricType ?? 'Pressure'
  );

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      try {
        const minPressure = Number(newRow?.minPressure) || 0;
        const maxPressure = Number(newRow?.maxPressure) || 0;
        const warningPressure = Number(newRow?.warningPressure) || 0;
        const minTemperature = Number(newRow?.minTemperature) || 0;
        const maxTemperature = Number(newRow?.maxTemperature) || 0;
        const warningTemperature = Number(newRow?.warningTemperature) || 0;
        const isFloatMinPressure = Number.isInteger(minPressure);
        const isFloatMaxPressure = Number.isInteger(maxPressure);
        const isFloatWarningPressure = Number.isInteger(warningPressure);
        const isFloatMinTemperature = Number.isInteger(minTemperature);
        const isFloatMaxTemperature = Number.isInteger(maxTemperature);
        const isFloatWarningTemperature = Number.isInteger(warningTemperature);
        const isValidMinPressure =
          isNumber(minPressure) &&
          minPressure >= 0 &&
          (minPressure === 0 && maxPressure === 0
            ? true
            : minPressure < maxPressure);

        const isValidMaxPressure = isNumber(maxPressure) && maxPressure <= 1200;

        if (!isValidMinPressure || !isValidMaxPressure) {
          dispatch(
            showNotification({
              message: !isValidMinPressure
                ? 'Min pressure must be >= 0 and < Max pressure'
                : 'Max pressure must be <= 1200',
              type: 'error',
            })
          );
          setRowModesModel((prev) => ({
            ...prev,
            [oldRow.id]: { mode: GridRowModes.Edit },
          }));
          return oldRow;
        }

        const isValidMinTemperature =
          isNumber(minTemperature) &&
          (minTemperature === 0 && maxTemperature === 0
            ? true
            : minTemperature < maxTemperature);

        const isValidMaxTemperature =
          isNumber(maxTemperature) && maxTemperature <= 1200;

        if (!isValidMinTemperature || !isValidMaxTemperature) {
          dispatch(
            showNotification({
              message: !isValidMinTemperature
                ? 'Min temperature must be < Max temperature'
                : 'Max temperature must be <= 1200',
              type: 'error',
            })
          );
          setRowModesModel((prev) => ({
            ...prev,
            [oldRow.id]: { mode: GridRowModes.Edit },
          }));
          return oldRow;
        }
        if (
          !isFloatMinPressure ||
          !isFloatMaxPressure ||
          !isFloatWarningPressure ||
          !isFloatMinTemperature ||
          !isFloatMaxTemperature ||
          !isFloatWarningTemperature
        ) {
          dispatch(
            showNotification({
              message: t('min_and_max_pressure_must_be_integers'),
              type: 'error',
            })
          );
          setRowModesModel((prev) => ({
            ...prev,
            [oldRow.id]: { mode: GridRowModes.Edit },
          }));
          return oldRow;
        }

        const isValidWarningPressure =
          !TPMS_WARNING_CONFIG.pressure ||
          (isNumber(warningPressure) &&
            (warningPressure === 0 && minPressure === 0 && maxPressure === 0
              ? true
              : warningPressure > minPressure &&
                warningPressure < maxPressure));

        if (!isValidWarningPressure) {
          dispatch(
            showNotification({
              message: t(
                'warning_pressure_must_be_between_min_and_max_pressure'
              ),
              type: 'error',
            })
          );
          setRowModesModel((prev) => ({
            ...prev,
            [oldRow.id]: { mode: GridRowModes.Edit },
          }));
          return oldRow;
        }

        const isValidWarningTemperature =
          !TPMS_WARNING_CONFIG.temperature ||
          (isNumber(warningTemperature) &&
            (warningTemperature === 0 &&
            minTemperature === 0 &&
            maxTemperature === 0
              ? true
              : warningTemperature > minTemperature &&
                warningTemperature < maxTemperature));

        if (!isValidWarningTemperature) {
          dispatch(
            showNotification({
              message: t(
                'warning_temperature_must_be_between_min_and_max_temperature'
              ),
              type: 'error',
            })
          );
          setRowModesModel((prev) => ({
            ...prev,
            [oldRow.id]: { mode: GridRowModes.Edit },
          }));
          return oldRow;
        }

        const payload = {
          selectedType: newRow?.assetType,
          selectedMake: newRow?.make,
          selectedModel: newRow?.model,
          minPressure: minPressure.toString(),
          maxPressure: maxPressure.toString(),
          warningPressure: warningPressure.toString(),
          minTemperature: minTemperature.toString(),
          maxTemperature: maxTemperature.toString(),
          warningTemperature: warningTemperature.toString(),
        };
        await updatePressureLimitForRow(payload);
        dispatch(
          showNotification({
            message: t('pressure_limits_updated'),
            type: 'success',
          })
        );

        return {
          ...oldRow,
          ...newRow,
          minPressure,
          maxPressure,
          warningPressure,
          minTemperature,
          maxTemperature,
          warningTemperature,
        };
      } catch (error) {
        dispatch(
          showNotification({
            message: t('error_while_updating'),
            type: 'error',
          })
        );
        setRowModesModel((prev) => ({
          ...prev,
          [oldRow.id]: { mode: GridRowModes.Edit },
        }));
        return oldRow;
      }
    },
    [rowModesModel, setRowModesModel]
  );

  useEffect(() => {
    const uniqueAssetsMap = new Map<string, Asset>();
    assets.forEach((asset) => {
      const key = `${asset.assetType}-${asset.make}-${asset.model}`;
      if (!uniqueAssetsMap.has(key)) {
        uniqueAssetsMap.set(key, asset);
      }
    });

    const uniqueAssetEntries = Array.from(uniqueAssetsMap.values());
    const customerLimits = (limits as any)?.customerLimits || [];
    const globalLimits = (limits as any)?.globalLimits || [];

    const processedRowData = uniqueAssetEntries.map(
      (asset): PressureLimitRowData => {
        const customerAssetLimits = customerLimits || [];
        const globalAssetLimits = globalLimits || [];

        let matchingLimit = customerAssetLimits.find(
          (limit: any) =>
            limit.make === asset.make && limit.model === asset.model
        );

        // fallback to global limit in case not found in customer limit
        if (!matchingLimit) {
          matchingLimit = globalAssetLimits.find(
            (limit: any) =>
              limit.make === asset.make && limit.model === asset.model
          );
        }

        const rowId = `row-${asset.assetType}-${asset.make}-${asset.model}`;

        return {
          id: rowId,
          assetType: asset.assetType,
          make: asset.make,
          model: asset.model,
          minPressure: Number(matchingLimit?.minPressure) || 0,
          maxPressure: Number(matchingLimit?.maxPressure) || 0,
          oemMinPressure:
            Number(matchingLimit?.oemRecommendedMinPressure) || 600, // incase it does not exist in any of the customer or global limit
          oemMaxPressure:
            Number(matchingLimit?.oemRecommendedMaxPressure) || 900, // incase it does not exist in any of the customer or global limit
          minTemperature: Number(matchingLimit?.minTemperature) || 0,
          maxTemperature:
            typeof matchingLimit?.maxTemperature === 'number'
              ? Number(matchingLimit.maxTemperature)
              : Number(matchingLimit?.maxTemperature) || 0,
          oemMinTemperature:
            Number(matchingLimit?.oemRecommendedMinTemperature) || 20, // incase it does not exist in any of the customer or global limit
          oemMaxTemperature:
            Number(matchingLimit?.oemRecommendedMaxTemperature) || 80, // incase it does not exist in any of the customer or global limit
          warningPressure: Number(matchingLimit?.warningPressure) || 0,
          warningTemperature: Number(matchingLimit?.warningTemperature) || 0,
        };
      }
    );

    // temp added gdy, lhl, sav to show values always in kpi even stored in be in kpi #r1
    const convertedProcessedData: PressureLimitRowData[] =
      unitSystem !== 'metric'
        ? processedRowData.map((item) => {
            return {
              ...item,
              maxPressure: Math.round(
                Number(item.maxPressure) !== 0
                  ? convertKpaToPsi(Number(item.maxPressure))
                  : 0
              ),
              minPressure: Math.round(
                Number(item.minPressure) !== 0
                  ? convertKpaToPsi(Number(item.minPressure))
                  : 0
              ),
              oemMaxPressure: Math.round(
                Number(item.oemMaxPressure) !== 0
                  ? convertKpaToPsi(Number(item.oemMaxPressure))
                  : 0
              ),
              oemMinPressure: Math.round(
                Number(item.oemMinPressure) !== 0
                  ? convertKpaToPsi(Number(item.oemMinPressure))
                  : 0
              ),
              maxTemperature: Math.round(
                Number(item.maxTemperature) !== 0
                  ? convertCentToFahr(Number(item.maxTemperature))
                  : 0
              ),
              minTemperature: Math.round(
                Number(item.minTemperature) !== 0
                  ? convertCentToFahr(Number(item.minTemperature))
                  : 0
              ),
              oemMaxTemperature: Math.round(
                Number(item.oemMaxTemperature) !== 0
                  ? convertCentToFahr(Number(item.oemMaxTemperature))
                  : 0
              ),
              oemMinTemperature: Math.round(
                Number(item.oemMinTemperature) !== 0
                  ? convertCentToFahr(Number(item.oemMinTemperature))
                  : 0
              ),
              warningPressure: Math.round(
                Number(item.warningPressure) !== 0
                  ? convertKpaToPsi(Number(item.warningPressure))
                  : 0
              ),
              warningTemperature: Math.round(
                Number(item.warningTemperature) !== 0
                  ? convertCentToFahr(Number(item.warningTemperature))
                  : 0
              ),
            };
          })
        : processedRowData;
    setRowData(convertedProcessedData);
  }, [assets, limits, unitSystem]);

  const commonColumns: GridColDef[] = [
    {
      headerName: t('asset_type'),
      field: 'assetType',
      editable: false,
      flex: 1.5,
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: 'Make',
      field: 'make',
      editable: false,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: t('model_10'),
      field: 'model',
      editable: false,
      flex: 1,
      headerAlign: 'left',
      align: 'left',
    },
  ];

  const pressureColumns: GridColDef[] = [
    {
      headerName: `Min (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
      field: 'oemMinPressure',
      flex: 1.2,
      editable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: `Max (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
      field: 'oemMaxPressure',
      flex: 1.2,
      editable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: `Min (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
      field: 'minPressure',
      flex: 1.2,
      editable: true,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    ...(TPMS_WARNING_CONFIG.pressure
      ? [
          {
            headerName: `Warning (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
            field: 'warningPressure',
            flex: 1.2,
            editable: true,
            type: 'number',
            headerAlign: 'left',
            align: 'left',
          },
        ]
      : []),
    {
      headerName: `Max (${unitSystem === 'metric' ? 'KPA' : 'PSI'})`,
      field: 'maxPressure',
      flex: 1.2,
      editable: true,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
  ].filter(Boolean) as GridColDef[];

  const temperatureColumns: GridColDef[] = [
    {
      headerName: `Min (${unitSystem === 'metric' ? '°C' : '°F'})`,
      field: 'oemMinTemperature',
      flex: 1.2,
      editable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: `Max (${unitSystem === 'metric' ? '°C' : '°F'})`,
      field: 'oemMaxTemperature',
      flex: 1.2,
      editable: false,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    {
      headerName: `Min (${unitSystem === 'metric' ? '°C' : '°F'})`,
      field: 'minTemperature',
      flex: 1.2,
      editable: true,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
    ...(TPMS_WARNING_CONFIG.temperature
      ? [
          {
            headerName: `Warning (${unitSystem === 'metric' ? '°C' : '°F'})`,
            field: 'warningTemperature',
            flex: 1.2,
            editable: true,
            type: 'number',
            headerAlign: 'left',
            align: 'left',
          },
        ]
      : []),
    {
      headerName: `Max (${unitSystem === 'metric' ? '°C' : '°F'})`,
      field: 'maxTemperature',
      flex: 1.2,
      editable: true,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
    },
  ].filter(Boolean) as GridColDef[];

  const actionColumn: GridColDef = {
    field: 'actions',
    type: 'actions',
    headerName: '',
    width: 100,
    headerAlign: 'center',
    align: 'center',
    cellClassName: 'actions',
    getActions: ({ id }) => {
      const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem
            icon={<Save />}
            label={t('save')}
            color="success"
            key={`0-save-button`}
            onClick={handleSaveClick(id)}
          />,
          <GridActionsCellItem
            icon={<Cancel />}
            label={t('cancel')}
            className="textPrimary"
            onClick={handleCancelClick(id)}
            key={`0-cancel-button`}
            color="error"
          />,
        ];
      }

      return [
        <GridActionsCellItem
          icon={<Edit />}
          label={t('edit')}
          className="textPrimary"
          onClick={handleEditClick(id)}
          color="inherit"
          key={`0-edit-button`}
        />,
      ];
    },
  };

  const columnDefs: GridColDef[] = [
    ...commonColumns,
    ...(metricType === 'Temperature' ? temperatureColumns : pressureColumns),
    actionColumn,
  ];

  const modalBgColor = enableDarkTheme
    ? palette.modalBackground || '#2c2c2c'
    : palette.toolBarBackground || '#ffffff';
  const modalTextColor = enableDarkTheme
    ? ThemePalette.typography.wheat || '#e0e0e0'
    : ThemePalette.typography.black || '#000000';
  const modalTitleBgColor = enableDarkTheme
    ? ThemePalette.dark.topBar
    : palette.toolBarBackground || '#f5f5f5';
  const modalBorderColor = enableDarkTheme
    ? ThemePalette.dark.cardBorderColor || '#444444'
    : palette.borderGrey || '#dddddd';

  return (
    <Dialog
      open={open}
      onClose={handleAbort}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: modalBgColor,
          color: modalTextColor,
          borderRadius: '12px',
          border: `1px solid ${modalBorderColor}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: modalTitleBgColor,
          borderBottom: `1px solid ${modalBorderColor}`,
          padding: '12px 24px',
          fontSize: '1.25rem',
          fontWeight: 500,
        }}
      >
        Tire {metricType} Limits
        <Button
          sx={(theme) => ({
            border: `1px solid ${modalBorderColor}`,
            marginLeft: '30px',
            paddingLeft: '10px',
            paddingRight: '10px',
            color: modalTextColor,
            textTransform: 'none',
            fontSize: '12px',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          })}
          onClick={() => {
            setMetricType(
              metricType === 'Pressure' ? 'Temperature' : 'Pressure'
            );
          }}
        >
          {metricType === 'Pressure' ? 'View Temperature' : 'View Pressure'}
        </Button>
        <IconButton
          aria-label="close"
          onClick={handleAbort}
          sx={{
            position: 'absolute',
            right: 12,
            top: 12,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '0px', minHeight: '400px' }}>
        <div
          style={{
            height: 'calc(100vh - 330px)',
            maxHeight: '400px',
            width: '100%',
          }}
        >
          <CustomStyledDataGrid
            columns={columnDefs}
            rows={rowData}
            disableColumnMenu={true}
            pagination
            rowModesModel={rowModesModel}
            editMode="row"
            processRowUpdate={processRowUpdate}
            disableRowSelectionOnClick
            rowHeight={45}
            columnHeaderHeight={30}
            columnGroupingModel={
              metricType === 'Pressure'
                ? columnGroupingModel
                : temperatureColumnGroupingModel
            }
            experimentalFeatures={{ columnGrouping: true }}
            showColumnVerticalBorder
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
